import { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { useApp, ADMIN_EMAIL } from "../../contexts/AppContext";
import { supabase } from "../../lib/supabase";

type Tab = "dashboard" | "users" | "products" | "messages" | "reports" | "news" | "categories";

const tabs: { id: Tab; label: string; icon: string }[] = [
  { id: "dashboard", label: "ダッシュボード", icon: "ri-dashboard-3-line" },
  { id: "users", label: "ユーザー管理", icon: "ri-user-settings-line" },
  { id: "products", label: "出品管理", icon: "ri-store-2-line" },
  { id: "messages", label: "一斉メッセージ", icon: "ri-broadcast-line" },
  { id: "reports", label: "通報管理", icon: "ri-alarm-warning-line" },
  { id: "news", label: "お知らせ管理", icon: "ri-notification-3-line" },
  { id: "categories", label: "カテゴリ管理", icon: "ri-folder-settings-line" },
];

/* ── Dashboard ───────────────────────────────────────────── */
function DashboardTab() {
  const [stats, setStats] = useState({
    totalUsers: 0, providers: 0, products: 0, pendingReports: 0,
    monthSales: 0, pendingApprovals: 0,
  });
  const [loading, setLoading] = useState(true);
  const [recentReports, setRecentReports] = useState<{ id: string; target_title: string; reason: string; created_at: string }[]>([]);
  const [recentNews, setRecentNews] = useState<{ id: string; title: string; tag: string; status: string; created_at: string }[]>([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [
        { count: totalUsers },
        { count: providers },
        { count: products },
        { count: pendingReports },
        { count: pendingApprovals },
        { data: salesData },
        { data: reports },
        { data: news },
      ] = await Promise.all([
        supabase.from("profiles").select("id", { count: "exact", head: true }),
        supabase.from("profiles").select("id", { count: "exact", head: true }).eq("role", "provider"),
        supabase.from("products").select("id", { count: "exact", head: true }),
        supabase.from("reports").select("id", { count: "exact", head: true }).eq("status", "pending"),
        supabase.from("products").select("id", { count: "exact", head: true }).eq("status", "review"),
        supabase.from("purchases").select("price").eq("status", "completed")
          .gte("purchased_at", new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString()),
        supabase.from("reports").select("id, target_title, reason, created_at").eq("status", "pending").order("created_at", { ascending: false }).limit(3),
        supabase.from("news").select("id, title, tag, status, created_at").order("created_at", { ascending: false }).limit(4),
      ]);
      const monthSales = (salesData || []).reduce((s, p) => s + (p.price || 0), 0);
      setStats({
        totalUsers: totalUsers || 0, providers: providers || 0, products: products || 0,
        pendingReports: pendingReports || 0, monthSales, pendingApprovals: pendingApprovals || 0,
      });
      setRecentReports(reports || []);
      setRecentNews(news || []);
      setLoading(false);
    };
    load();
  }, []);

  const statCards = [
    { label: "総ユーザー数", value: stats.totalUsers.toLocaleString(), icon: "ri-group-line", color: "text-teal" },
    { label: "事業者数", value: stats.providers.toLocaleString(), icon: "ri-store-2-line", color: "text-gold" },
    { label: "今月売上総額", value: `¥${stats.monthSales.toLocaleString()}`, icon: "ri-money-yen-circle-line", color: "text-deep-green" },
    { label: "未処理通報", value: `${stats.pendingReports}件`, icon: "ri-alarm-warning-line", color: "text-red-400" },
    { label: "出品数", value: stats.products.toLocaleString(), icon: "ri-shopping-bag-line", color: "text-teal" },
    { label: "審査待ち商品", value: `${stats.pendingApprovals}件`, icon: "ri-megaphone-line", color: "text-gold" },
  ];

  if (loading) return <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-teal" /></div>;

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        {statCards.map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-6 border border-stone-100">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-stone-50">
                <i className={`${s.icon} text-xl ${s.color}`} />
              </div>
            </div>
            <p className="font-serif text-2xl text-charcoal mb-1">{s.value}</p>
            <p className="text-xs text-charcoal/50 font-sans">{s.label}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-stone-100">
          <h3 className="font-serif text-deep-green text-lg mb-4 flex items-center gap-2">
            <i className="ri-alarm-warning-line text-red-400" />最新通報（要対応）
          </h3>
          {recentReports.length === 0 ? (
            <p className="text-sm text-charcoal/40 font-sans py-4 text-center">未処理の通報はありません</p>
          ) : (
            <div className="space-y-3">
              {recentReports.map(r => (
                <div key={r.id} className="flex items-start gap-3 p-3 bg-red-50 rounded-xl border border-red-100">
                  <i className="ri-error-warning-line text-red-400 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-sans text-charcoal font-medium truncate">{r.target_title}</p>
                    <p className="text-xs text-charcoal/50 font-sans">{r.reason} · {new Date(r.created_at).toLocaleDateString("ja-JP")}</p>
                  </div>
                  <span className="text-xs bg-red-100 text-red-500 px-2 py-0.5 rounded-full font-sans whitespace-nowrap">未処理</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white rounded-2xl p-6 border border-stone-100">
          <h3 className="font-serif text-deep-green text-lg mb-4 flex items-center gap-2">
            <i className="ri-notification-3-line text-gold" />最新お知らせ
          </h3>
          <div className="space-y-3">
            {recentNews.map(n => (
              <div key={n.id} className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl">
                <span className={`text-xs px-2 py-0.5 rounded-full font-sans whitespace-nowrap ${n.status === "published" ? "bg-teal/10 text-teal" : "bg-stone-200 text-charcoal/50"}`}>
                  {n.status === "published" ? "公開中" : "下書き"}
                </span>
                <p className="text-sm font-sans text-charcoal flex-1 truncate">{n.title}</p>
                <p className="text-xs text-charcoal/40 font-sans whitespace-nowrap">{new Date(n.created_at).toLocaleDateString("ja-JP")}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Users ───────────────────────────────────────────────── */
function UsersTab() {
  const [users, setUsers] = useState<{ id: string; username: string; name: string; email: string; role: string; plan: string; subscription_status: string; purpose?: string; created_at: string; product_count?: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const loadUsers = useCallback(async () => {
    setLoading(true);
    let query = supabase.from("profiles").select("id, username, name, email, role, plan, subscription_status, purpose, created_at").order("created_at", { ascending: false });
    if (roleFilter !== "all") query = query.eq("role", roleFilter);
    const { data } = await query;
    const users = data || [];

    // Get product counts per provider
    const { data: productCounts } = await supabase
      .from("products")
      .select("provider_id");
    const countMap: Record<string, number> = {};
    (productCounts || []).forEach(p => {
      countMap[p.provider_id] = (countMap[p.provider_id] || 0) + 1;
    });

    setUsers(users.map(u => ({ ...u, product_count: countMap[u.id] || 0 })));
    setLoading(false);
  }, [roleFilter]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleRoleChange = async (userId: string, newRole: string) => {
    await supabase.from("profiles").update({ role: newRole }).eq("id", userId);
    loadUsers();
  };

  const PURPOSE_LABELS: Record<string, string> = {
    hobby: "趣味として学びたい", skill: "手に職をつけたい", career: "将来の仕事につなげたい",
    return: "復職の準備をしたい", change: "転職・キャリアチェンジ", business: "独立・副業の参考に",
    succession: "家業・事業承継", research: "情報収集したい", other: "その他",
  };

  const filtered = users.filter(u =>
    u.name?.includes(search) || u.email?.includes(search) || u.username?.includes(search)
  );

  return (
    <div>
      <div className="mb-4 flex items-center gap-2 p-4 bg-teal/5 border border-teal/20 rounded-2xl">
        <i className="ri-information-line text-teal text-base" />
        <p className="text-xs text-charcoal/70 font-sans">登録は一本化されています。全員が受講者として登録し、事業者モードに切り替えることで商品を出品できます。「出品あり」バッジで出品経験者を確認できます。</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40 text-sm" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="名前・メール・ユーザー名で検索"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal bg-white" />
        </div>
        <div className="flex gap-1 bg-stone-100 p-1 rounded-full">
          {[{ v: "all", l: "全員" }, { v: "consumer", l: "受講者" }, { v: "provider", l: "事業者" }].map(f => (
            <button key={f.v} onClick={() => setRoleFilter(f.v)}
              className={`px-4 py-1.5 rounded-full text-xs font-sans cursor-pointer transition-all whitespace-nowrap ${roleFilter === f.v ? "bg-white text-deep-green" : "text-charcoal/50 hover:text-charcoal"}`}>{f.l}</button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-teal" /></div>
      ) : (
        <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-100">
                <tr>
                  {["ユーザー", "メール", "種別", "出品状況", "利用目的", "プラン", "登録日", "操作"].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-sans text-charcoal/50 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-charcoal/40 font-sans">ユーザーが見つかりません</td></tr>
                ) : filtered.map(u => (
                  <tr key={u.id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-sm font-sans text-charcoal font-medium">{u.name || "—"}</p>
                      <p className="text-xs text-charcoal/40 font-sans">@{u.username || "—"}</p>
                    </td>
                    <td className="px-4 py-3 text-xs font-sans text-charcoal/60">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-sans ${u.role === "provider" ? "bg-gold/10 text-gold" : u.role === "admin" ? "bg-deep-green/10 text-deep-green" : "bg-teal/10 text-teal"}`}>
                        {u.role === "provider" ? "事業者" : u.role === "admin" ? "管理者" : "受講者"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {(u.product_count ?? 0) > 0 ? (
                        <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-sans whitespace-nowrap border border-amber-200">
                          <i className="ri-store-2-line mr-0.5" />出品あり ({u.product_count})
                        </span>
                      ) : (
                        <span className="text-xs text-charcoal/30 font-sans">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs font-sans text-charcoal/60 max-w-[140px]">
                      {u.purpose ? (
                        <span className="bg-deep-green/8 text-deep-green px-2 py-0.5 rounded-full text-xs font-sans whitespace-nowrap">
                          {PURPOSE_LABELS[u.purpose] || u.purpose}
                        </span>
                      ) : <span className="text-charcoal/30">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs font-sans text-charcoal/60">
                      {u.plan === "light" ? "ライト" : "フリー"}
                      {u.subscription_status === "past_due" && <span className="ml-1 text-red-400">（支払遅延）</span>}
                    </td>
                    <td className="px-4 py-3 text-xs font-sans text-charcoal/50">{new Date(u.created_at).toLocaleDateString("ja-JP")}</td>
                    <td className="px-4 py-3">
                      <select
                        value={u.role}
                        onChange={e => handleRoleChange(u.id, e.target.value)}
                        className="text-xs border border-stone-200 rounded-lg px-2 py-1 font-sans cursor-pointer focus:outline-none focus:border-teal bg-white"
                      >
                        <option value="consumer">受講者</option>
                        <option value="provider">事業者</option>
                        <option value="admin">管理者</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Products ────────────────────────────────────────────── */
function ProductsTab() {
  const [products, setProducts] = useState<{ id: string; title: string; provider_id: string; type: string; price: number; status: string; created_at: string; profiles?: { name: string } }[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const loadProducts = useCallback(async () => {
    setLoading(true);
    let query = supabase.from("products").select("id, title, provider_id, type, price, status, created_at, profiles:provider_id(name)").order("created_at", { ascending: false });
    if (typeFilter !== "all") query = query.eq("type", typeFilter);
    if (statusFilter !== "all") query = query.eq("status", statusFilter);
    const { data } = await query;
    setProducts((data || []) as typeof products);
    setLoading(false);
  }, [typeFilter, statusFilter]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const handleStatusChange = async (productId: string, newStatus: string) => {
    await supabase.from("products").update({ status: newStatus }).eq("id", productId);
    loadProducts();
  };

  const handleDelete = async (productId: string) => {
    if (!confirm("この商品を削除しますか？この操作は取り消せません。")) return;
    await supabase.from("products").delete().eq("id", productId);
    loadProducts();
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex gap-1 bg-stone-100 p-1 rounded-full">
          {[{ v: "all", l: "すべて" }, { v: "video", l: "動画" }, { v: "kit", l: "キット" }].map(f => (
            <button key={f.v} onClick={() => setTypeFilter(f.v)}
              className={`px-4 py-1.5 rounded-full text-xs font-sans cursor-pointer transition-all whitespace-nowrap ${typeFilter === f.v ? "bg-white text-deep-green" : "text-charcoal/50 hover:text-charcoal"}`}>{f.l}</button>
          ))}
        </div>
        <div className="flex gap-1 bg-stone-100 p-1 rounded-full">
          {[{ v: "all", l: "全状態" }, { v: "published", l: "公開中" }, { v: "review", l: "審査中" }, { v: "rejected", l: "却下" }].map(f => (
            <button key={f.v} onClick={() => setStatusFilter(f.v)}
              className={`px-4 py-1.5 rounded-full text-xs font-sans cursor-pointer transition-all whitespace-nowrap ${statusFilter === f.v ? "bg-white text-deep-green" : "text-charcoal/50 hover:text-charcoal"}`}>{f.l}</button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-teal" /></div>
      ) : (
        <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-100">
                <tr>
                  {["商品名", "事業者", "種別", "価格", "状態", "出品日", "操作"].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-sans text-charcoal/50 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {products.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-charcoal/40 font-sans">商品がありません</td></tr>
                ) : products.map(p => (
                  <tr key={p.id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-4 py-3 text-sm font-sans text-charcoal font-medium max-w-[200px] truncate">{p.title}</td>
                    <td className="px-4 py-3 text-xs font-sans text-charcoal/60 whitespace-nowrap">{(p.profiles as { name?: string } | undefined)?.name || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-sans ${p.type === "video" ? "bg-teal/10 text-teal" : "bg-gold/10 text-gold"}`}>
                        {p.type === "video" ? "動画" : "キット"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-sans text-charcoal">¥{p.price?.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <select
                        value={p.status}
                        onChange={e => handleStatusChange(p.id, e.target.value)}
                        className={`text-xs border rounded-lg px-2 py-1 font-sans cursor-pointer focus:outline-none bg-white ${p.status === "published" ? "border-green-200 text-green-600" : p.status === "review" ? "border-amber-200 text-amber-600" : "border-red-200 text-red-500"}`}
                      >
                        <option value="published">公開中</option>
                        <option value="review">審査中</option>
                        <option value="rejected">却下</option>
                        <option value="draft">下書き</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-xs font-sans text-charcoal/50">{new Date(p.created_at).toLocaleDateString("ja-JP")}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleDelete(p.id)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 text-red-400 cursor-pointer transition-colors">
                        <i className="ri-delete-bin-line text-xs" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Reports ─────────────────────────────────────────────── */
function ReportsTab() {
  const [reports, setReports] = useState<{ id: string; target_type: string; target_title: string; reason: string; detail: string; status: string; created_at: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const loadReports = useCallback(async () => {
    setLoading(true);
    let query = supabase.from("reports").select("id, target_type, target_title, reason, detail, status, created_at").order("created_at", { ascending: false });
    if (filter !== "all") query = query.eq("status", filter);
    const { data } = await query;
    setReports(data || []);
    setLoading(false);
  }, [filter]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const updateStatus = async (id: string, status: string) => {
    await supabase.from("reports").update({ status }).eq("id", id);
    loadReports();
  };

  const statusLabel: Record<string, string> = { pending: "未処理", reviewed: "確認済", resolved: "解決済" };
  const statusColor: Record<string, string> = { pending: "bg-red-100 text-red-500", reviewed: "bg-amber-100 text-amber-600", resolved: "bg-green-100 text-green-600" };

  return (
    <div>
      <div className="flex gap-1 bg-stone-100 p-1 rounded-full mb-6 w-fit">
        {[{ v: "all", l: "すべて" }, { v: "pending", l: "未処理" }, { v: "reviewed", l: "確認済" }, { v: "resolved", l: "解決済" }].map(f => (
          <button key={f.v} onClick={() => setFilter(f.v)}
            className={`px-4 py-1.5 rounded-full text-xs font-sans cursor-pointer transition-all whitespace-nowrap ${filter === f.v ? "bg-white text-deep-green" : "text-charcoal/50 hover:text-charcoal"}`}>{f.l}</button>
        ))}
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-teal" /></div>
      ) : reports.length === 0 ? (
        <div className="text-center py-16 text-charcoal/40 font-sans text-sm">通報がありません</div>
      ) : (
        <div className="space-y-3">
          {reports.map(r => (
            <div key={r.id} className="bg-white rounded-2xl p-5 border border-stone-100 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${r.status === "pending" ? "bg-red-50" : "bg-stone-50"}`}>
                <i className={`ri-alarm-warning-line text-xl ${r.status === "pending" ? "text-red-400" : "text-charcoal/30"}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-xs bg-stone-100 text-charcoal/50 px-2 py-0.5 rounded-full font-sans">{r.target_type}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-sans ${statusColor[r.status] || "bg-stone-100 text-charcoal/50"}`}>{statusLabel[r.status] || r.status}</span>
                </div>
                <p className="text-sm font-sans text-charcoal font-medium">{r.target_title}</p>
                <p className="text-xs text-charcoal/50 font-sans mt-0.5">理由：{r.reason}　{r.detail && `詳細：${r.detail.slice(0, 50)}…`}</p>
                <p className="text-xs text-charcoal/40 font-sans mt-0.5">{new Date(r.created_at).toLocaleDateString("ja-JP")}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                {r.status === "pending" && (
                  <>
                    <button onClick={() => updateStatus(r.id, "reviewed")} className="px-3 py-1.5 rounded-lg bg-teal/10 text-teal text-xs font-sans cursor-pointer hover:bg-teal/20 transition-colors whitespace-nowrap">確認済にする</button>
                    <button onClick={() => updateStatus(r.id, "resolved")} className="px-3 py-1.5 rounded-lg bg-red-50 text-red-400 text-xs font-sans cursor-pointer hover:bg-red-100 transition-colors whitespace-nowrap">解決済にする</button>
                  </>
                )}
                {r.status === "reviewed" && (
                  <button onClick={() => updateStatus(r.id, "resolved")} className="px-3 py-1.5 rounded-lg bg-green-50 text-green-600 text-xs font-sans cursor-pointer hover:bg-green-100 transition-colors whitespace-nowrap">解決済にする</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── News ────────────────────────────────────────────────── */
function NewsTab() {
  const { currentUser } = useApp();
  const [items, setItems] = useState<{ id: string; title: string; tag: string; status: string; content: string; created_at: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<typeof items[0] | null>(null);
  const [form, setForm] = useState({ title: "", tag: "お知らせ", status: "draft", content: "" });
  const [saving, setSaving] = useState(false);

  const loadNews = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from("news").select("id, title, tag, status, content, created_at").order("created_at", { ascending: false });
    setItems(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadNews(); }, [loadNews]);

  const handleSave = async () => {
    if (!form.title) return;
    setSaving(true);
    if (editItem) {
      await supabase.from("news").update({ title: form.title, tag: form.tag, status: form.status, content: form.content, updated_at: new Date().toISOString() }).eq("id", editItem.id);
    } else {
      await supabase.from("news").insert({ title: form.title, tag: form.tag, status: form.status, content: form.content, created_by: currentUser?.id });
    }
    setSaving(false);
    setShowForm(false);
    setEditItem(null);
    setForm({ title: "", tag: "お知らせ", status: "draft", content: "" });
    loadNews();
  };

  const handleEdit = (item: typeof items[0]) => {
    setEditItem(item);
    setForm({ title: item.title, tag: item.tag, status: item.status, content: item.content || "" });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("このお知らせを削除しますか？")) return;
    await supabase.from("news").delete().eq("id", id);
    loadNews();
  };

  const TAG_OPTIONS = ["お知らせ", "特集", "イベント", "キャンペーン", "アップデート"];

  return (
    <div>
      <div className="flex justify-end mb-6">
        <button onClick={() => { setEditItem(null); setForm({ title: "", tag: "お知らせ", status: "draft", content: "" }); setShowForm(true); }}
          className="flex items-center gap-2 bg-deep-green text-white text-sm px-4 py-2.5 rounded-full font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap">
          <i className="ri-add-line" />新規作成
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl p-6 border border-teal/30 mb-6">
          <p className="font-serif text-deep-green text-base mb-4">{editItem ? "編集" : "新規作成"}</p>
          <div className="flex flex-col gap-4">
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="タイトル"
              className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal" />
            <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} placeholder="本文（省略可）" rows={4} maxLength={500}
              className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal resize-none" />
            <div className="flex gap-3">
              <select value={form.tag} onChange={e => setForm(f => ({ ...f, tag: e.target.value }))}
                className="flex-1 px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal cursor-pointer">
                {TAG_OPTIONS.map(t => <option key={t}>{t}</option>)}
              </select>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                className="flex-1 px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal cursor-pointer">
                <option value="draft">下書き</option>
                <option value="published">公開</option>
              </select>
            </div>
            <p className="text-xs text-charcoal/40 font-sans">
              <i className="ri-information-line mr-1 text-teal" />
              「アップデート」タグを選ぶとトップページのアップデート情報欄に表示されます。
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setShowForm(false); setEditItem(null); }} className="px-4 py-2 rounded-full border border-stone-200 text-sm font-sans text-charcoal/60 cursor-pointer">キャンセル</button>
              <button onClick={handleSave} disabled={saving}
                className="px-5 py-2 rounded-full bg-deep-green text-white text-sm font-sans cursor-pointer hover:bg-teal transition-colors disabled:opacity-60">
                {saving ? "保存中..." : "保存"}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-teal" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-charcoal/40 font-sans text-sm">お知らせがありません</div>
      ) : (
        <div className="space-y-3">
          {items.map(n => (
            <div key={n.id} className="bg-white rounded-2xl p-4 border border-stone-100 flex items-center gap-4">
              <span className={`text-xs px-2 py-0.5 rounded-full font-sans whitespace-nowrap ${n.tag === "特集" ? "bg-teal/10 text-teal" : n.tag === "イベント" ? "bg-gold/10 text-gold" : n.tag === "キャンペーン" ? "bg-deep-green/10 text-deep-green" : n.tag === "アップデート" ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-charcoal/50"}`}>{n.tag}</span>
              <p className="text-sm font-sans text-charcoal flex-1">{n.title}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full font-sans ${n.status === "published" ? "bg-green-100 text-green-600" : "bg-stone-200 text-charcoal/50"}`}>{n.status === "published" ? "公開中" : "下書き"}</span>
              <p className="text-xs text-charcoal/40 font-sans whitespace-nowrap">{new Date(n.created_at).toLocaleDateString("ja-JP")}</p>
              <div className="flex gap-1">
                <button onClick={() => handleEdit(n)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-stone-100 hover:bg-stone-200 text-charcoal/50 cursor-pointer">
                  <i className="ri-edit-line text-xs" />
                </button>
                <button onClick={() => handleDelete(n.id)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 text-red-400 cursor-pointer">
                  <i className="ri-delete-bin-line text-xs" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Categories ──────────────────────────────────────────── */
function CategoriesTab() {
  const [cats, setCats] = useState<{ id: string; name: string; description: string; icon: string; visible: boolean; sort_order: number; status: string; created_by: string | null; requester?: { name: string; email: string } | null }[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCat, setNewCat] = useState({ name: "", description: "", icon: "ri-folder-line" });
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState<"all" | "approved" | "pending">("all");

  const loadCats = useCallback(async () => {
    setLoading(true);
    const { data: catData } = await supabase
      .from("categories")
      .select("id, name, description, icon, visible, sort_order, status, created_by")
      .order("sort_order", { ascending: true });
    const cats = catData || [];
    // Fetch requester info for pending categories
    const creatorIds = [...new Set(cats.filter(c => c.created_by).map(c => c.created_by as string))];
    let profileMap: Record<string, { name: string; email: string }> = {};
    if (creatorIds.length > 0) {
      const { data: profiles } = await supabase.from("profiles").select("id, name, email").in("id", creatorIds);
      (profiles || []).forEach(p => { profileMap[p.id] = { name: p.name, email: p.email }; });
    }
    setCats(cats.map(c => ({ ...c, requester: c.created_by ? profileMap[c.created_by] || null : null })));
    setLoading(false);
  }, []);

  useEffect(() => { loadCats(); }, [loadCats]);

  const toggleVisible = async (id: string, current: boolean) => {
    await supabase.from("categories").update({ visible: !current }).eq("id", id);
    loadCats();
  };

  const approveCategory = async (id: string) => {
    await supabase.from("categories").update({ status: "approved", visible: true }).eq("id", id);
    loadCats();
  };

  const rejectCategory = async (id: string) => {
    await supabase.from("categories").update({ status: "rejected", visible: false }).eq("id", id);
    loadCats();
  };

  const handleAddCategory = async () => {
    if (!newCat.name) return;
    setSaving(true);
    const maxOrder = cats.length > 0 ? Math.max(...cats.map(c => c.sort_order)) : 0;
    await supabase.from("categories").insert({ name: newCat.name, description: newCat.description, icon: newCat.icon, visible: true, sort_order: maxOrder + 1, status: "approved" });
    setSaving(false);
    setShowAddForm(false);
    setNewCat({ name: "", description: "", icon: "ri-folder-line" });
    loadCats();
  };

  const pendingCount = cats.filter(c => c.status === "pending" || c.status === "approved").length - cats.filter(c => c.status === "approved").length;
  const filtered = filterStatus === "all" ? cats : filterStatus === "pending" ? cats.filter(c => c.status === "pending") : cats.filter(c => c.status === "approved");

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="flex gap-1 bg-stone-100 p-1 rounded-full">
            {[{ v: "all" as const, l: "すべて" }, { v: "approved" as const, l: "承認済" }, { v: "pending" as const, l: "承認待ち" }].map(f => (
              <button key={f.v} onClick={() => setFilterStatus(f.v)}
                className={`px-4 py-1.5 rounded-full text-xs font-sans cursor-pointer transition-all whitespace-nowrap flex items-center gap-1.5 ${filterStatus === f.v ? "bg-white text-deep-green" : "text-charcoal/50 hover:text-charcoal"}`}>
                {f.l}
                {f.v === "pending" && pendingCount > 0 && (
                  <span className="bg-red-400 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center leading-none">{pendingCount}</span>
                )}
              </button>
            ))}
          </div>
        </div>
        <button onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-2 bg-deep-green text-white text-sm px-4 py-2.5 rounded-full font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap">
          <i className="ri-add-line" />カテゴリを追加
        </button>
      </div>

      {pendingCount > 0 && filterStatus !== "pending" && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5 flex items-center gap-3">
          <i className="ri-notification-3-line text-amber-600 text-xl" />
          <div className="flex-1">
            <p className="text-sm font-sans font-medium text-amber-800">事業者からの申請が {pendingCount}件 あります</p>
            <p className="text-xs text-amber-600 font-sans mt-0.5">「承認待ち」フィルターで確認できます</p>
          </div>
          <button onClick={() => setFilterStatus("pending")}
            className="text-xs bg-amber-600 text-white px-3 py-1.5 rounded-full font-sans cursor-pointer hover:bg-amber-700 whitespace-nowrap">
            確認する
          </button>
        </div>
      )}

      {showAddForm && (
        <div className="bg-white rounded-2xl p-6 border border-teal/30 mb-6">
          <p className="font-serif text-deep-green text-base mb-4">新規カテゴリを追加（管理者直接追加）</p>
          <div className="flex flex-col gap-4">
            <input value={newCat.name} onChange={e => setNewCat(f => ({ ...f, name: e.target.value }))} placeholder="カテゴリ名（例：染色・染め物）"
              className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal" />
            <input value={newCat.description} onChange={e => setNewCat(f => ({ ...f, description: e.target.value }))} placeholder="説明文（省略可）"
              className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowAddForm(false)} className="px-4 py-2 rounded-full border border-stone-200 text-sm font-sans text-charcoal/60 cursor-pointer">キャンセル</button>
              <button onClick={handleAddCategory} disabled={saving}
                className="px-5 py-2 rounded-full bg-deep-green text-white text-sm font-sans cursor-pointer hover:bg-teal transition-colors disabled:opacity-60">
                {saving ? "追加中..." : "追加する"}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-teal" /></div>
      ) : (
        <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-100">
                <tr>
                  {["カテゴリ名", "説明", "申請者", "状態", "操作"].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-sans text-charcoal/50 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {filtered.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-charcoal/40 font-sans">カテゴリがありません</td></tr>
                ) : filtered.map(c => (
                  <tr key={c.id} className={`hover:bg-stone-50 transition-colors ${c.status === "pending" ? "bg-amber-50/30" : ""}`}>
                    <td className="px-4 py-3 text-sm font-sans text-charcoal font-medium">{c.name}</td>
                    <td className="px-4 py-3 text-xs font-sans text-charcoal/60 max-w-[180px] truncate">{c.description || "—"}</td>
                    <td className="px-4 py-3">
                      {c.requester ? (
                        <div>
                          <p className="text-xs font-sans text-charcoal font-medium">{c.requester.name || "—"}</p>
                          <p className="text-xs font-sans text-charcoal/40">{c.requester.email}</p>
                        </div>
                      ) : (
                        <span className="text-xs text-charcoal/30 font-sans">管理者</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-sans ${c.status === "approved" ? (c.visible ? "bg-green-100 text-green-600" : "bg-stone-200 text-charcoal/50") : c.status === "pending" ? "bg-amber-100 text-amber-600" : "bg-red-100 text-red-500"}`}>
                        {c.status === "pending" ? "承認待ち" : c.status === "rejected" ? "却下" : c.visible ? "表示中" : "非表示"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5">
                        {c.status === "pending" && (
                          <>
                            <button onClick={() => approveCategory(c.id)}
                              className="px-2.5 py-1 text-xs bg-teal/10 text-teal rounded-lg cursor-pointer hover:bg-teal hover:text-white transition-colors whitespace-nowrap">承認</button>
                            <button onClick={() => rejectCategory(c.id)}
                              className="px-2.5 py-1 text-xs bg-red-50 text-red-400 rounded-lg cursor-pointer hover:bg-red-100 transition-colors whitespace-nowrap">却下</button>
                          </>
                        )}
                        {c.status !== "pending" && (
                          <button onClick={() => toggleVisible(c.id, c.visible)}
                            className={`w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${c.visible ? "bg-red-50 hover:bg-red-100 text-red-400" : "bg-green-50 hover:bg-green-100 text-green-500"}`}
                            title={c.visible ? "非表示にする" : "表示する"}>
                            <i className={`${c.visible ? "ri-eye-off-line" : "ri-eye-line"} text-xs`} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Admin Messages (Broadcast) ──────────────────────────── */
const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;

function MessagesTab() {
  const { session } = useApp();
  const [form, setForm] = useState({
    subject: "",
    message: "",
    target_role: "all",
    send_in_app: true,
    send_email: false,
  });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ success?: boolean; in_app_count?: number; email_count?: number; total_users?: number; error?: string } | null>(null);
  const [userCount, setUserCount] = useState<{ all: number; consumer: number; provider: number }>({ all: 0, consumer: 0, provider: 0 });

  useEffect(() => {
    const load = async () => {
      const [{ count: all }, { count: consumer }, { count: provider }] = await Promise.all([
        supabase.from("profiles").select("id", { count: "exact", head: true }).neq("email", ADMIN_EMAIL),
        supabase.from("profiles").select("id", { count: "exact", head: true }).eq("role", "consumer"),
        supabase.from("profiles").select("id", { count: "exact", head: true }).eq("role", "provider"),
      ]);
      setUserCount({ all: all ?? 0, consumer: consumer ?? 0, provider: provider ?? 0 });
    };
    load();
  }, []);

  const targetCount = form.target_role === "all" ? userCount.all : form.target_role === "consumer" ? userCount.consumer : userCount.provider;

  const handleSend = async () => {
    if (!form.subject.trim() || !form.message.trim()) return;
    if (!confirm(`${targetCount}名に送信します。よろしいですか？`)) return;
    setSending(true);
    setResult(null);
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const res = await fetch(`${SUPABASE_URL}/functions/v1/admin-broadcast`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authSession?.access_token}`,
        },
        body: JSON.stringify({
          message: form.message,
          subject: form.subject,
          target_role: form.target_role,
          send_in_app: form.send_in_app,
          send_email: form.send_email,
        }),
      });
      const data = await res.json();
      setResult(data);
      if (data.success) {
        setForm({ subject: "", message: "", target_role: "all", send_in_app: true, send_email: false });
      }
    } catch (e) {
      setResult({ error: (e as Error).message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 flex items-start gap-3">
        <i className="ri-broadcast-line text-amber-600 text-xl mt-0.5" />
        <div>
          <p className="text-sm font-sans font-semibold text-amber-800">一斉メッセージ送信</p>
          <p className="text-xs text-amber-600 font-sans mt-0.5">全ユーザー・受講者・事業者など対象を選んでアプリ内メッセージまたはメールを一括送信できます。管理者からのメッセージとして届きます。</p>
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className={`rounded-2xl p-4 mb-6 flex items-start gap-3 ${result.success ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
          <i className={`text-xl mt-0.5 ${result.success ? "ri-checkbox-circle-line text-green-600" : "ri-error-warning-line text-red-400"}`} />
          <div>
            {result.success ? (
              <>
                <p className="text-sm font-sans font-semibold text-green-800">送信完了！</p>
                <p className="text-xs text-green-600 font-sans mt-0.5">
                  対象 {result.total_users}名 → アプリ内 {result.in_app_count}件
                  {(result.email_count ?? 0) > 0 && ` / メール ${result.email_count}件`} 送信しました
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-sans font-semibold text-red-700">送信に失敗しました</p>
                <p className="text-xs text-red-500 font-sans">{result.error}</p>
              </>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl p-6 border border-stone-100 space-y-5">
        {/* Target */}
        <div>
          <label className="block text-xs font-sans text-charcoal/60 mb-2 font-medium">送信対象</label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: "all", l: "全ユーザー", count: userCount.all, icon: "ri-group-line" },
              { v: "consumer", l: "受講者のみ", count: userCount.consumer, icon: "ri-graduation-cap-line" },
              { v: "provider", l: "事業者のみ", count: userCount.provider, icon: "ri-store-2-line" },
            ].map(opt => (
              <button
                key={opt.v}
                onClick={() => setForm(f => ({ ...f, target_role: opt.v }))}
                className={`border rounded-xl p-3 text-left cursor-pointer transition-all ${form.target_role === opt.v ? "border-deep-green bg-deep-green/5" : "border-stone-200 hover:border-stone-300"}`}
              >
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className={`${opt.icon} text-base ${form.target_role === opt.v ? "text-deep-green" : "text-charcoal/40"}`} />
                </div>
                <p className={`text-xs font-sans mt-1.5 font-medium ${form.target_role === opt.v ? "text-deep-green" : "text-charcoal/60"}`}>{opt.l}</p>
                <p className="text-xs text-charcoal/40 font-sans">{opt.count.toLocaleString()}名</p>
              </button>
            ))}
          </div>
        </div>

        {/* Subject */}
        <div>
          <label className="block text-xs font-sans text-charcoal/60 mb-1.5 font-medium">件名（メール・アプリ内通知のタイトル） <span className="text-red-400">*</span></label>
          <input
            value={form.subject}
            onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
            placeholder="例：サービス開始のお知らせ"
            maxLength={100}
            className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal"
          />
        </div>

        {/* Message */}
        <div>
          <label className="block text-xs font-sans text-charcoal/60 mb-1.5 font-medium">メッセージ本文 <span className="text-red-400">*</span></label>
          <textarea
            value={form.message}
            onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
            placeholder="ユーザーに届けたい内容を入力してください。"
            rows={6}
            maxLength={500}
            className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal resize-none"
          />
          <p className="text-xs text-charcoal/40 font-sans text-right mt-1">{form.message.length}/500</p>
        </div>

        {/* Send options */}
        <div>
          <label className="block text-xs font-sans text-charcoal/60 mb-2 font-medium">送信方法</label>
          <div className="space-y-2">
            <label className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl cursor-pointer hover:bg-stone-100 transition-colors">
              <input
                type="checkbox"
                checked={form.send_in_app}
                onChange={e => setForm(f => ({ ...f, send_in_app: e.target.checked }))}
                className="w-4 h-4 accent-deep-green"
              />
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-message-3-line text-deep-green" />
                </div>
                <div>
                  <p className="text-xs font-sans text-charcoal font-medium">アプリ内メッセージ</p>
                  <p className="text-xs text-charcoal/40 font-sans">マイページのメッセージ欄に届きます</p>
                </div>
              </div>
            </label>
            <label className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl cursor-pointer hover:bg-stone-100 transition-colors">
              <input
                type="checkbox"
                checked={form.send_email}
                onChange={e => setForm(f => ({ ...f, send_email: e.target.checked }))}
                className="w-4 h-4 accent-deep-green"
              />
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-mail-line text-gold" />
                </div>
                <div>
                  <p className="text-xs font-sans text-charcoal font-medium">メール通知</p>
                  <p className="text-xs text-charcoal/40 font-sans">RESEND_API_KEY設定が必要です</p>
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Preview */}
        {form.message && (
          <div className="bg-deep-green/5 border border-deep-green/20 rounded-xl p-4">
            <p className="text-xs text-deep-green font-sans font-medium mb-2">プレビュー</p>
            <p className="text-xs text-charcoal/50 font-sans mb-1">管理者（TSUGITE運営）：</p>
            <p className="text-sm text-charcoal font-sans whitespace-pre-wrap">{form.message}</p>
          </div>
        )}

        <button
          onClick={handleSend}
          disabled={sending || !form.subject.trim() || !form.message.trim() || (!form.send_in_app && !form.send_email)}
          className="w-full py-3.5 rounded-full bg-deep-green text-white text-sm font-sans cursor-pointer hover:bg-teal transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
        >
          {sending ? (
            <><i className="ri-loader-4-line animate-spin" />送信中...</>
          ) : (
            <><i className="ri-send-plane-2-line" />{targetCount}名に送信する</>
          )}
        </button>
      </div>
    </div>
  );
}

/* ── Main Page ───────────────────────────────────────────── */
export default function AdminPage() {
  const [activeTab, setActiveTab] = useState<Tab>("dashboard");
  const { currentUser, authLoading } = useApp();

  if (authLoading) {
    return <div className="min-h-screen bg-ivory flex items-center justify-center"><i className="ri-loader-4-line animate-spin text-3xl text-teal" /></div>;
  }

  if (!currentUser || currentUser.email !== ADMIN_EMAIL) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-ivory">
      <Navbar />
      <main className="pt-20">
        <section className="bg-deep-green px-4 py-8">
          <div className="max-w-screen-xl mx-auto flex items-center justify-between">
            <div>
              <p className="font-display italic text-gold text-xs tracking-widest mb-1">Admin Panel</p>
              <h1 className="font-serif text-white text-2xl font-light">管理者ダッシュボード</h1>
              <p className="text-white/40 text-sm font-sans mt-1">TSUGITE 運営管理 — リアルタイムデータ</p>
            </div>
            <span className="text-xs bg-gold/20 text-gold px-3 py-1.5 rounded-full font-sans">
              <i className="ri-shield-user-line mr-1" />管理者
            </span>
          </div>
        </section>

        <section className="bg-white border-b border-stone-100 sticky top-16 z-30 px-4">
          <div className="max-w-screen-xl mx-auto flex gap-1 overflow-x-auto">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 px-4 py-4 text-sm font-sans whitespace-nowrap cursor-pointer border-b-2 transition-all duration-200 ${activeTab === t.id ? "border-deep-green text-deep-green" : "border-transparent text-charcoal/50 hover:text-charcoal"}`}>
                <i className={t.icon} />{t.label}
              </button>
            ))}
          </div>
        </section>

        <section className="px-4 py-8">
          <div className="max-w-screen-xl mx-auto">
            {activeTab === "dashboard" && <DashboardTab />}
            {activeTab === "users" && <UsersTab />}
            {activeTab === "products" && <ProductsTab />}
            {activeTab === "messages" && <MessagesTab />}
            {activeTab === "reports" && <ReportsTab />}
            {activeTab === "news" && <NewsTab />}
            {activeTab === "categories" && <CategoriesTab />}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
